import { EnvelopedDataSetting } from 'components/node_settings/V4EditSettingsModal/AdvancedSettingsTab/EnvelopedDataSetting';
import { SectionDescription } from 'components/node_settings/V4EditSettingsModal/styled';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { SettingWarnings } from 'components/node_settings/V4Settings/SettingWarnings';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import { ToggleSetting } from 'components/node_settings/V4Settings/ToggleSetting';
import {
    DEFAULT_TIMED,
    UPDATE_INTERVAL_MODE_LABELS,
    UpdateIntervalMode,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

type AxisName = 'vibrationX' | 'vibrationY' | 'vibrationZ';

export type AxisEffects = Partial<
    Record<AxisName, { envelopedDisabled: boolean; lfcDisabled: boolean }>
>;
export type IntervalChange = {
    from: Exclude<UpdateIntervalMode, 'timed'>;
    axis: AxisName;
} | null;

type AdvancedSettingsProps = Readonly<{
    axisEffectsState?: [AxisEffects, Dispatch<SetStateAction<AxisEffects>>];
    intervalChangeState?: [IntervalChange, Dispatch<SetStateAction<IntervalChange>>];
    children?: ReactNode;
}>;

export function AdvancedSettings({
    axisEffectsState,
    intervalChangeState,
    children,
}: AdvancedSettingsProps) {
    const { control, setValue, getValues } = useFormContext<V4SensorSettings>();
    const [vibrationX, vibrationY, vibrationZ, ultrasoundEnveloping, updateInterval] =
        useWatch({
            control,
            name: [
                'vibrationX',
                'vibrationY',
                'vibrationZ',
                'ultrasoundEnveloping',
                'updateInterval',
            ],
        });

    const internalAxisEffects = useState<AxisEffects>({});
    const [axisEffects, setAxisEffects] = axisEffectsState ?? internalAxisEffects;
    const internalIntervalChange = useState<IntervalChange>(null);
    const [intervalChange, setIntervalChange] =
        intervalChangeState ?? internalIntervalChange;

    useEffect(() => {
        if (updateInterval.mode !== 'timed') {
            setIntervalChange(null);
        }
    }, [updateInterval.mode, setIntervalChange]);

    const setAxis = (axis: AxisName, next: boolean) => {
        const prior = getValues();
        const nextAxes = {
            vibrationX: prior.vibrationX,
            vibrationY: prior.vibrationY,
            vibrationZ: prior.vibrationZ,
            [axis]: next,
        };
        const allAxesOff =
            !nextAxes.vibrationX && !nextAxes.vibrationY && !nextAxes.vibrationZ;

        setValue(axis, next, { shouldDirty: true, shouldValidate: true });

        // Update Interval shifts to Timed when all axes are disabled because SMARTsample and
        // Triggered need sampling data. The warning shows on whichever axis emptied the set,
        // so that axis is tracked alongside the prior mode. Coerced here because the Collection
        // tab unmounts while this tab is active and can't enforce the all-axes-off rule itself.
        if (allAxesOff && prior.updateInterval.mode !== 'timed') {
            setValue('updateInterval', DEFAULT_TIMED, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setIntervalChange({ from: prior.updateInterval.mode, axis });
        }

        const clearAxisEffect = () =>
            setAxisEffects((prev) => {
                if (!(axis in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[axis];
                return next;
            });

        if (next) {
            clearAxisEffect();
            return;
        }

        const lfcDisabled = allAxesOff && prior.lowFrequencyCollection.enabled;
        const envelopedDisabled = axis === 'vibrationY' && prior.envelopedData.enabled;

        if (lfcDisabled) {
            setValue('lowFrequencyCollection', { enabled: false }, { shouldDirty: true });
        }
        if (envelopedDisabled) {
            setValue('envelopedData', { enabled: false }, { shouldDirty: true });
        }
        if (lfcDisabled || envelopedDisabled) {
            setAxisEffects((prev) => ({
                ...prev,
                [axis]: { envelopedDisabled, lfcDisabled },
            }));
        } else {
            clearAxisEffect();
        }
    };

    const axisWarnings = (axis: AxisName): ReactNode => {
        const messages: string[] = [];
        if (intervalChange?.axis === axis) {
            messages.push(
                V4_SETTINGS_TEXT.axisWarnings.updateIntervalChangedWarning(
                    UPDATE_INTERVAL_MODE_LABELS[intervalChange.from],
                ),
            );
        }
        const effects = axisEffects[axis];
        if (effects?.envelopedDisabled) {
            messages.push(V4_SETTINGS_TEXT.axisWarnings.envelopedNowDisabled);
        }
        if (effects?.lfcDisabled) {
            messages.push(V4_SETTINGS_TEXT.lowFrequencyCollection.nowDisabledWarning);
        }
        return messages.length > 0 ? (
            <SettingWarnings
                messages={messages}
                testId={`AdvancedSettings__warnings-${axis}`}
            />
        ) : undefined;
    };

    return (
        <>
            <SectionDescription>
                {V4_SETTINGS_TEXT.descriptions.advancedSettingsDescription}
            </SectionDescription>
            <SettingsList>
                <ToggleSetting
                    name={V4_SETTINGS_TEXT.settingNames.vibrationX}
                    ariaLabel={V4_SETTINGS_TEXT.settingNames.vibrationX}
                    helperText={axisWarnings('vibrationX')}
                    checked={vibrationX}
                    onChange={(checked) => setAxis('vibrationX', checked)}
                />
                <ToggleSetting
                    name={V4_SETTINGS_TEXT.settingNames.vibrationY}
                    ariaLabel={V4_SETTINGS_TEXT.settingNames.vibrationY}
                    helperText={axisWarnings('vibrationY')}
                    checked={vibrationY}
                    onChange={(checked) => setAxis('vibrationY', checked)}
                />
                <ToggleSetting
                    name={V4_SETTINGS_TEXT.settingNames.vibrationZ}
                    ariaLabel={V4_SETTINGS_TEXT.settingNames.vibrationZ}
                    helperText={axisWarnings('vibrationZ')}
                    checked={vibrationZ}
                    onChange={(checked) => setAxis('vibrationZ', checked)}
                />

                <EnvelopedDataSetting />

                <ToggleSetting
                    name={V4_SETTINGS_TEXT.settingNames.ultrasoundEnveloped}
                    ariaLabel={V4_SETTINGS_TEXT.settingNames.ultrasoundEnveloped}
                    helperText={V4_SETTINGS_TEXT.descriptions.ultrasoundHelper}
                    checked={ultrasoundEnveloping}
                    onChange={(checked) =>
                        setValue('ultrasoundEnveloping', checked, {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                />
                {children}
            </SettingsList>
        </>
    );
}
